package org.example.project.screens_cus_mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.enginekalender.EngineMonthCalendar
import org.example.project.enginekalender.kalenderBookingMarkerColorHex
import org.example.project.enginekalender.kalenderDayState
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginekalender.loadKalenderBookings
import org.example.project.enginekalender.parseKalenderIsoDate
import org.example.project.enginepersonal.ZeitApi
import org.example.project.enginepersonal.ZeitMobileDayPopup
import org.example.project.enginepersonal.ZeitMonth
import org.example.project.enginepersonal.ZeitMobileDayInfo
import org.example.project.enginepersonal.ZeitMobileMonthSummary
import org.example.project.enginepersonal.ZeitprofilConfig
import org.example.project.enginepersonal.defaultZeitprofil
import org.example.project.enginepersonal.zeitMobileDayInfo
import org.example.project.enginepersonal.zeitMobileProfileHourRange
import org.example.project.enginepersonal.zeitMobileMonthLabel
import org.example.project.enginepersonal.zeitMobileMonthSummary
import org.example.project.manager.NodeScope
import org.example.project.screens_cus.Abwesenheitsbedarf
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.ZeitkontoJournalEintrag
import org.example.project.service.LoginService
import org.example.project.service.GlobalManager
import org.example.project.service.UserApi
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import kotlin.js.Date
import kotlinx.browser.window

@Composable
private fun MitarbeiterMeinUrlaubMetric(label: String, value: String) {
    Div({ classes(MitarbeiterMeinUrlaubMobileStyles.metricItem) }) {
        Span({ classes(MitarbeiterMeinUrlaubMobileStyles.metricLabel) }) { Text(label) }
        Span({ classes(MitarbeiterMeinUrlaubMobileStyles.metricValue) }) { Text(value) }
    }
}

@Composable
private fun MitarbeiterMeinUrlaubHinweis(text: String, error: Boolean = false) {
    Div({
        classes(
            if (error) MitarbeiterMeinUrlaubMobileStyles.error
            else MitarbeiterMeinUrlaubMobileStyles.notice
        )
    }) { Text(text) }
}

private data class MitarbeiterAbwesenheitsart(
    val key: String,
    val label: String,
    val verbrauchtUrlaub: Boolean
)

private val mitarbeiterAbwesenheitsarten = listOf(
    MitarbeiterAbwesenheitsart("Urlaub", "Urlaub", true),
    MitarbeiterAbwesenheitsart("Freizeitausgleich", "Freizeitausgleich", false),
    MitarbeiterAbwesenheitsart("Unbezahlt frei", "Unbezahlt frei", false),
    MitarbeiterAbwesenheitsart("Sonderurlaub", "Sonderurlaub", false)
)

private val mitarbeiterBeantragbareAbwesenheitsarten = mitarbeiterAbwesenheitsarten
    .map { it.key.lowercase() }
    .toSet()

private const val mitarbeiterUrlaubBereichBeantragen = "beantragen"

private val mitarbeiterUrlaubBereichOptionen = listOf(
    All(key = mitarbeiterUrlaubBereichBeantragen, label = "Urlaub beantragen"),
    All(key = MitarbeiterUrlaubStatusGroup.OFFEN.name, label = "Offene Anträge"),
    All(key = MitarbeiterUrlaubStatusGroup.GENEHMIGT.name, label = "Genehmigte Anträge"),
    All(key = MitarbeiterUrlaubStatusGroup.STORNIERT.name, label = "Stornierte Anträge")
)

private fun istMitarbeiterBeantragbareAbwesenheit(antrag: All): Boolean {
    val abwesenheitsart = antrag.abwesenheitsart.ifBlank { antrag.bezeichnung.ifBlank { antrag.art } }
        .trim()
        .lowercase()
    return antrag.urlaubstag != false && abwesenheitsart in mitarbeiterBeantragbareAbwesenheitsarten
}

@Composable
fun MitarbeiterMeinUrlaub() {
    val userId = LoginService.getId()
    val refreshVersion = GlobalManager.jsonDomainVersion("urlaub") to
        GlobalManager.jsonDomainVersion("kalender")
    val currentYear = Date().getFullYear().toInt()
    var antraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var allowance by remember { mutableStateOf<org.example.project.screens_cus.Urlaubsanspruch?>(null) }
    var termine by remember { mutableStateOf<List<All>>(emptyList()) }
    var userNamesById by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf("") }
    var showRequest by remember { mutableStateOf(true) }
    var selectedStatus by remember { mutableStateOf(MitarbeiterUrlaubStatusGroup.OFFEN) }
    var reloadToken by remember { mutableStateOf(0) }
    var loadedOnce by remember { mutableStateOf(false) }

    LaunchedEffect(userId, refreshVersion, reloadToken) {
        if (!loadedOnce) loading = true
        error = ""
        runCatching {
            val userVacation = UrlaubsApi.loadMyYear(currentYear)
            val userAllowance = UrlaubsApi.loadMyAllowance(currentYear)
            val userCalendar = loadKalenderBookings(NodeScope.CUS, userId)
            val users = UserApi.loadUsers().associate { user ->
                user.id to mitarbeiterMeinUrlaubUserName(user)
            }
            Triple(userVacation, userAllowance, userCalendar) to users
        }.onSuccess { result ->
            antraege = result.first.first
                .sortedWith(
                    compareByDescending<All> { it.datumVon.ifBlank { it.datum } }
                        .thenByDescending { it.erstelltAm }
                )
            allowance = result.first.second
            termine = result.first.third
            userNamesById = result.second
            loadedOnce = true
        }.onFailure {
            antraege = emptyList()
            allowance = null
            termine = emptyList()
            userNamesById = emptyMap()
            error = it.message ?: "Urlaub konnte nicht geladen werden."
        }
        loading = false
    }

    Style(MitarbeiterMeinUrlaubMobileStyles); Style(MitarbeiterMeinUrlaubTabletStyles)
    Div({ classes(MitarbeiterMeinUrlaubMobileStyles.page, MitarbeiterMeinUrlaubTabletStyles.fullPage) }) {
        Div({ classes(MitarbeiterMeinUrlaubTabletStyles.mainHeader) }) {
            H3({ classes(MitarbeiterMeinUrlaubTabletStyles.mainTitle) }) { Text("Mein Urlaub") }
            Div({ classes(MitarbeiterMeinUrlaubMobileStyles.mobileSectionDropdown) }) {
                AppDropdown(
                    options = mitarbeiterUrlaubBereichOptionen,
                    selectedValue = if (showRequest) mitarbeiterUrlaubBereichBeantragen else selectedStatus.name,
                    onChange = { value ->
                        if (value == mitarbeiterUrlaubBereichBeantragen) {
                            showRequest = true
                        } else {
                            MitarbeiterUrlaubStatusGroup.entries.firstOrNull { it.name == value }?.let { group ->
                                showRequest = false
                                selectedStatus = group
                            }
                        }
                    },
                    appearance = AppDropdownAppearance.Field
                )
            }
            Div({ classes(MitarbeiterMeinUrlaubTabletStyles.mainButtonBar) }) {
                LumaticaButton(
                    label = "Urlaub beantragen",
                    onClick = { showRequest = true },
                    variant = LumaticaButtonVariant.State,
                    isActive = showRequest,
                    heightPx = 36
                )
                LumaticaButton(
                    label = "Offene Anträge",
                    onClick = {
                        showRequest = false
                        selectedStatus = MitarbeiterUrlaubStatusGroup.OFFEN
                    },
                    variant = LumaticaButtonVariant.State,
                    isActive = !showRequest && selectedStatus == MitarbeiterUrlaubStatusGroup.OFFEN,
                    heightPx = 36
                )
                LumaticaButton(
                    label = "Genehmigte Anträge",
                    onClick = {
                        showRequest = false
                        selectedStatus = MitarbeiterUrlaubStatusGroup.GENEHMIGT
                    },
                    variant = LumaticaButtonVariant.State,
                    isActive = !showRequest && selectedStatus == MitarbeiterUrlaubStatusGroup.GENEHMIGT,
                    heightPx = 36
                )
                LumaticaButton(
                    label = "Stornierte Anträge",
                    onClick = {
                        showRequest = false
                        selectedStatus = MitarbeiterUrlaubStatusGroup.STORNIERT
                    },
                    variant = LumaticaButtonVariant.State,
                    isActive = !showRequest && selectedStatus == MitarbeiterUrlaubStatusGroup.STORNIERT,
                    heightPx = 36
                )
            }
        }

        when {
            loading -> MitarbeiterMeinUrlaubHinweis("Urlaub wird geladen.")
            error.isNotBlank() -> MitarbeiterMeinUrlaubHinweis(error, error = true)
            else -> {
                if (showRequest) {
                    MitarbeiterUrlaubDialog(
                        userId = userId,
                        initialDate = kalenderTodayIsoDate(),
                        termine = termine,
                        antraege = antraege,
                        belegteUrlaubstage = kalenderDayState(termine).bookings
                            .filter { mitarbeiterIstGanztagsUrlaub(it) }
                            .map { mitarbeiterKalenderDatum(it.datum) },
                        embedded = true,
                        onClose = { showRequest = false },
                        onSaved = {
                            showRequest = false
                            selectedStatus = MitarbeiterUrlaubStatusGroup.OFFEN
                            reloadToken += 1
                        }
                    )
                } else {
                    MitarbeiterUrlaubAntragsbereiche(
                        antraege = antraege.filter(::istMitarbeiterBeantragbareAbwesenheit),
                        selectedStatus = selectedStatus,
                        userNamesById = userNamesById,
                    )
                }
            }
        }
    }
}


@Composable
private fun MitarbeiterUrlaubDialog(
    userId: String,
    initialDate: String,
    termine: List<All>,
    antraege: List<All>,
    belegteUrlaubstage: List<String>,
    embedded: Boolean = false,
    onClose: () -> Unit,
    onSaved: () -> Unit
) {
    val startDate = initialDate.takeIf { parseKalenderIsoDate(it) != null } ?: kalenderTodayIsoDate()
    var von by remember(startDate) { mutableStateOf(startDate) }
    var bis by remember(startDate) { mutableStateOf(startDate) }
    var pickerMonth by remember(startDate) { mutableStateOf(startDate.take(7)) }
    var selectingEnd by remember { mutableStateOf(false) }
    var abwesenheitsart by remember { mutableStateOf(mitarbeiterAbwesenheitsarten.first().key) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }
    var allowances by remember(userId) {
        mutableStateOf<Map<Int, org.example.project.screens_cus.Urlaubsanspruch>>(emptyMap())
    }
    var zeitkontoBedarf by remember { mutableStateOf<Abwesenheitsbedarf?>(null) }
    var calendarRef by remember { mutableStateOf<HTMLDivElement?>(null) }
    val scope = rememberCoroutineScope()
    var calendarAbsences by remember(antraege) { mutableStateOf(antraege) }
    LaunchedEffect(userId, pickerMonth, antraege) {
        val year = pickerMonth.take(4).toIntOrNull() ?: return@LaunchedEffect
        val loaded = UrlaubsApi.loadMyYear(year)
        calendarAbsences = loaded + calendarAbsences.filter { old -> loaded.none { it.id == old.id } }
    }
    val absenceDays = remember(calendarAbsences) {
        calendarAbsences.filter { it.urlaubstag != false }.flatMap { absence ->
            val from = absence.datumVon.ifBlank { absence.datum }.take(10)
            val to = absence.datumBis.ifBlank { from }.take(10)
            if (parseKalenderIsoDate(from) == null || parseKalenderIsoDate(to) == null || from > to) emptyList()
            else buildList {
                var day = from
                while (day <= to) {
                    add(absence.copy(datum = day, urlaub = true, farbe = null))
                    day = mitarbeiterShiftIsoDate(day, 1)
                }
            }
        }
    }
    val dayStates = remember(termine, absenceDays) {
        val absenceIds = calendarAbsences.map { it.id }.filter { it.isNotBlank() }.toSet()
        (absenceDays + termine.filter { it.urlaubId !in absenceIds })
            .groupBy { mitarbeiterKalenderDatum(it.datum) }
            .mapValues { (day, bookings) ->
                val state = kalenderDayState(bookings)
                val absenceColor = kalenderDayState(absenceDays.filter { it.datum == day }).colorHex
                state.copy(colorHex = absenceColor ?: state.colorHex)
            }
    }
    val occupiedDays = remember(absenceDays, belegteUrlaubstage) {
        belegteUrlaubstage.toSet() + kalenderDayState(absenceDays).bookings.map { it.datum }
    }
    val tage = remember(von, bis) { mitarbeiterKalenderWerktage(von, bis) }
    val requestedByYear = remember(tage) {
        tage.groupingBy { it.take(4).toIntOrNull() ?: 0 }
            .eachCount()
            .filterKeys { it > 0 }
    }
    val requestedYears = requestedByYear.keys.sorted()
    val statsYear = von.take(4).toIntOrNull() ?: Date().getFullYear().toInt()
    val yearsToLoad = remember(requestedYears, statsYear) {
        (requestedYears + statsYear).distinct().sorted()
    }
    val verbrauchtUrlaub = mitarbeiterAbwesenheitsarten
        .firstOrNull { it.key == abwesenheitsart }
        ?.verbrauchtUrlaub != false
    val brauchtZeitkonto = abwesenheitsart == "Freizeitausgleich"
    LaunchedEffect(userId, yearsToLoad) {
        allowances = yearsToLoad.associateWith { year ->
            UrlaubsApi.loadMyAllowance(year)
        }
    }
    LaunchedEffect(userId, von, bis) {
        zeitkontoBedarf = UrlaubsApi.loadMyAbsenceRequirement(von, bis, "Freizeitausgleich")
    }
    val hatBelegtenUrlaubstag = remember(tage, occupiedDays) {
        tage.any { tag -> tag in occupiedDays }
    }
    val anspruchGeladen = requestedYears.all { it in allowances }
    val urlaubsanspruchOk = !verbrauchtUrlaub || anspruchGeladen
    val anspruchUeberschritten = verbrauchtUrlaub && anspruchGeladen && requestedByYear.any { (year, requestedDays) ->
        requestedDays > (allowances[year]?.verfuegbar ?: 0.0)
    }
    val tagesarbeitszeitFehlt = brauchtZeitkonto && zeitkontoBedarf?.profileFehlt == true
    val zeitkontoNichtGeladen = brauchtZeitkonto && zeitkontoBedarf?.ok != true
    val zeitkontoUeberschritten = brauchtZeitkonto && zeitkontoBedarf?.ok == true &&
        (zeitkontoBedarf?.stundenBedarf ?: 0.0) > (zeitkontoBedarf?.zeitkontoVerfuegbar ?: 0.0)
    val statsAllowance = allowances[statsYear]
    val angezeigtesZeitkonto = zeitkontoBedarf?.takeIf { it.ok }?.zeitkonto
    val angezeigteZeitreserve = zeitkontoBedarf?.takeIf { it.ok }?.zeitreserve
    val reservierteUeberstunden = zeitkontoBedarf?.takeIf { it.ok }?.reservierteUeberstunden
    val verfuegbaresZeitkonto = zeitkontoBedarf?.takeIf { it.ok }?.zeitkontoVerfuegbar
    val kontostandNachBuchungText = when {
        brauchtZeitkonto && zeitkontoBedarf?.ok == true -> {
            val nachBuchung = (angezeigtesZeitkonto ?: 0.0) -
                (angezeigteZeitreserve ?: 0.0) -
                (reservierteUeberstunden ?: 0.0) -
                (zeitkontoBedarf?.stundenBedarf ?: 0.0)
            mitarbeiterMeinUrlaubStundenText(nachBuchung)
        }
        verbrauchtUrlaub && anspruchGeladen -> {
            val verfuegbar = statsAllowance?.verfuegbar ?: 0.0
            val tageImAngezeigtenJahr = requestedByYear[statsYear]?.toDouble() ?: 0.0
            mitarbeiterUrlaubstageText(verfuegbar - tageImAngezeigtenJahr)
        }
        else -> "-"
    }
    val abwesenheitsLabel = mitarbeiterAbwesenheitsarten
        .firstOrNull { it.key == abwesenheitsart }
        ?.label
        ?: abwesenheitsart
    val buchungsgrundOptionen = remember {
        mitarbeiterAbwesenheitsarten.map { option -> All(key = option.key, label = option.label) }
    }

    fun focusCalendar() {
        window.setTimeout({
            calendarRef?.asDynamic()?.scrollIntoView(
                js("{ behavior: 'smooth', block: 'start', inline: 'nearest' }")
            )
        }, 0)
    }

    fun selectDay(day: String) {
        if (!selectingEnd) {
            von = day
            bis = day
            selectingEnd = true
            message = ""
            return
        }
        if (mitarbeiterIsoLessThan(day, von)) {
            bis = von
            von = day
        } else {
            bis = day
        }
        selectingEnd = false
        message = ""
    }

    Div({
        classes(
            if (embedded) MitarbeiterMeinUrlaubTabletStyles.requestPanel else MitarbeiterMeinUrlaubMobileStyles.overlay
        )
    }) {
        Div({
            classes(
                if (embedded) MitarbeiterMeinUrlaubTabletStyles.requestForm else MitarbeiterMeinUrlaubMobileStyles.dialog
            )
        }) {
            Div({ classes(MitarbeiterMeinUrlaubTabletStyles.requestMetricsRow) }) {
                Div({ classes(MitarbeiterMeinUrlaubMobileStyles.metricGroup) }) {
                    Span({ classes(MitarbeiterMeinUrlaubMobileStyles.metricGroupTitle) }) { Text("Mein Kontostand") }
                    Div({ classes(MitarbeiterMeinUrlaubMobileStyles.metricGrid) }) {
                        MitarbeiterMeinUrlaubMetric("Anspruch", statsAllowance?.gesamtanspruch?.let(::mitarbeiterUrlaubstageText) ?: "-")
                        MitarbeiterMeinUrlaubMetric("Urlaub verfügbar", statsAllowance?.verfuegbar?.let(::mitarbeiterUrlaubstageText) ?: "-")
                        MitarbeiterMeinUrlaubMetric("Überstunden", verfuegbaresZeitkonto?.let(::mitarbeiterMeinUrlaubStundenText) ?: "-")
                    }
                }
                Div({ classes(MitarbeiterMeinUrlaubMobileStyles.metricGroup, MitarbeiterMeinUrlaubTabletStyles.requestReasonGroup) }) {
                    Span({ classes(MitarbeiterMeinUrlaubMobileStyles.metricGroupTitle) }) { Text("Buchungsgrund") }
                    AppDropdown(
                        options = buchungsgrundOptionen,
                        selectedValue = abwesenheitsart,
                        onChange = {
                            abwesenheitsart = it
                            focusCalendar()
                        },
                        enabled = !saving,
                        appearance = AppDropdownAppearance.Field
                    )
                }
            }

            Div({ classes(MitarbeiterMeinUrlaubTabletStyles.requestWorkspace) }) {
                Div({
                    classes(MitarbeiterMeinUrlaubTabletStyles.requestCard)
                    ref { element ->
                        calendarRef = element
                        onDispose {
                            if (calendarRef == element) calendarRef = null
                        }
                    }
                }) {
                    Span({ classes(MitarbeiterMeinUrlaubTabletStyles.requestCardTitle) }) { Text("Kalender") }
                    Div({ classes(MitarbeiterMeinUrlaubMobileStyles.rangeHeader) }) {
                        LumaticaButton(
                            label = "<",
                            onClick = { pickerMonth = mitarbeiterShiftMonth(pickerMonth, -1) },
                            variant = LumaticaButtonVariant.Standard,
                            heightPx = 30,
                            paddingH = 10,
                            enabled = !saving
                        )
                        Span({ classes(MitarbeiterMeinUrlaubMobileStyles.rangeMonthLabel) }) {
                            Text(mitarbeiterMonthLabel(pickerMonth))
                        }
                        LumaticaButton(
                            label = ">",
                            onClick = { pickerMonth = mitarbeiterShiftMonth(pickerMonth, 1) },
                            variant = LumaticaButtonVariant.Standard,
                            heightPx = 30,
                            paddingH = 10,
                            enabled = !saving
                        )
                    }
                    Div({ classes(MitarbeiterMeinUrlaubTabletStyles.requestCalendarGrid) }) {
                        listOf("Mo", "Di", "Mi", "Do", "Fr", "Sa", "So").forEach { label ->
                            Span({ classes(MitarbeiterMeinUrlaubMobileStyles.rangeWeekday) }) { Text(label) }
                        }
                        mitarbeiterMeinUrlaubTabletMonthCells(pickerMonth).forEach { day ->
                            if (day.isBlank()) {
                                Span({ classes(MitarbeiterMeinUrlaubMobileStyles.rangeEmptyDay) }) {}
                            } else {
                                val isStart = day == von
                                val isEnd = day == bis
                                val inRange = !mitarbeiterIsoLessThan(day, von) && !mitarbeiterIsoLessThan(bis, day)
                                val date = mitarbeiterDateFromIso(day)
                                val weekend = date.getDay().toInt() in setOf(0, 6)
                                val alreadyBooked = day in occupiedDays
                                val dayState = dayStates[day]
                                val bookingDescription = dayState?.bookings?.joinToString("; ") {
                                    "${it.abwesenheitsart.ifBlank { it.bezeichnung.ifBlank { it.art } }}: " +
                                        org.example.project.enginekalender.kalenderBookingStatusText(it)
                                }.orEmpty()
                                Button(attrs = {
                                    classes(MitarbeiterMeinUrlaubMobileStyles.rangeDay)
                                    if (weekend) classes(MitarbeiterMeinUrlaubMobileStyles.rangeDayMuted)
                                    if (alreadyBooked) classes(MitarbeiterMeinUrlaubMobileStyles.rangeDayBooked)
                                    if (inRange) classes(MitarbeiterMeinUrlaubMobileStyles.rangeDayInRange)
                                    if (isStart || isEnd) classes(MitarbeiterMeinUrlaubMobileStyles.rangeDayEdge)
                                    if (alreadyBooked) dayState?.colorHex?.let { statusColor ->
                                        style {
                                            backgroundColor(Color(statusColor))
                                            color(Color.white)
                                            property("text-decoration", "none")
                                            property("opacity", "1")
                                        }
                                    }
                                    if (dayState != null && dayState.count > 0) {
                                        attr("title", bookingDescription)
                                        attr("aria-label", "${mitarbeiterMeinUrlaubFullDateLabel(day)}: $bookingDescription")
                                    }
                                    if (saving) attr("disabled", "true")
                                    onClick {
                                        selectDay(day)
                                        if (alreadyBooked) message = "Am ${mitarbeiterMeinUrlaubFullDateLabel(day)} ist bereits Urlaub/Abwesenheit gebucht. $bookingDescription"
                                    }
                                }) {
                                    Text(day.takeLast(2).trimStart('0').ifBlank { "0" })
                                    dayState?.colorHex?.let { markerColor ->
                                        Span({
                                            classes(MitarbeiterMeinUrlaubMobileStyles.rangeDayEventMarker)
                                            attr("aria-hidden", "true")
                                            style { backgroundColor(Color(markerColor)) }
                                        }) {}
                                    }
                                }
                            }
                        }
                    }
                }

                Div({ classes(MitarbeiterMeinUrlaubTabletStyles.requestCard) }) {
                    Div({ classes(MitarbeiterMeinUrlaubTabletStyles.requestEventSummary) }) {
                        MitarbeiterMeinUrlaubEventRow("Start", mitarbeiterMeinUrlaubFullDateLabel(von))
                        MitarbeiterMeinUrlaubEventRow("Ende", mitarbeiterMeinUrlaubFullDateLabel(bis))
                        if (brauchtZeitkonto) {
                            MitarbeiterMeinUrlaubEventRow(
                                "Freizeitausgleich",
                                zeitkontoBedarf?.takeIf { it.ok }?.stundenBedarf?.let(::mitarbeiterMeinUrlaubStundenText) ?: "-"
                            )
                        } else {
                            MitarbeiterMeinUrlaubEventRow("Urlaubstage", tage.size.toString())
                        }
                        MitarbeiterMeinUrlaubEventRow(
                            "Kontostand nach Buchung",
                            kontostandNachBuchungText
                        )
                    }
                    val visibleMessage = message.ifBlank {
                        if (hatBelegtenUrlaubstag) "Im ausgewählten Zeitraum ist bereits Urlaub/Abwesenheit gebucht. Bitte einen freien Zeitraum wählen." else ""
                    }
                    if (visibleMessage.isNotBlank()) {
                        Div({ classes(MitarbeiterMeinUrlaubMobileStyles.dialogMessage) }) { Text(visibleMessage) }
                    }
                    Div({ classes(MitarbeiterMeinUrlaubMobileStyles.dialogActions, MitarbeiterMeinUrlaubTabletStyles.requestActions) }) {
                        LumaticaButton("Abbrechen", onClick = onClose, variant = LumaticaButtonVariant.Standard, enabled = !saving)
                        LumaticaButton(
                            label = if (saving) "Speichere..." else "Beantragen",
                            onClick = {
                                if (tage.isEmpty()) {
                                    message = "Bitte einen Zeitraum mit Arbeitstagen wählen."
                                    return@LumaticaButton
                                }
                                if (hatBelegtenUrlaubstag) {
                                    message = "Dieser Zeitraum ist bereits im Kalender reserviert."
                                    return@LumaticaButton
                                }
                                if (anspruchUeberschritten) {
                                    message = "Der verfügbare Urlaubsanspruch reicht für diesen Zeitraum nicht aus."
                                    return@LumaticaButton
                                }
                                if (zeitkontoUeberschritten) {
                                    message = "Die verfügbaren Überstunden reichen nach Abzug von Zeitreserve und reservierten Überstunden nicht aus."
                                    return@LumaticaButton
                                }
                                if (zeitkontoNichtGeladen) {
                                    message = "Zeitkonto konnte nicht geladen werden."
                                    return@LumaticaButton
                                }
                                if (tagesarbeitszeitFehlt) {
                                    message = "Freizeitausgleich ist nicht möglich, weil keine Tagesarbeitszeit hinterlegt ist."
                                    return@LumaticaButton
                                }
                                saving = true
                                message = ""
                                scope.launch {
                                    val result = UrlaubsApi.requestVacationResult(
                                        userId = userId,
                                        datumVon = von,
                                        datumBis = bis,
                                        abwesenheitsart = abwesenheitsart
                                    )
                                    saving = false
                                    if (result.first) onSaved() else message = result.second
                                }
                            },
                            variant = LumaticaButtonVariant.Action,
                            enabled = !saving && tage.isNotEmpty() && !hatBelegtenUrlaubstag &&
                                urlaubsanspruchOk && !anspruchUeberschritten && !zeitkontoUeberschritten &&
                                !zeitkontoNichtGeladen && !tagesarbeitszeitFehlt
                        )
                    }
                }
            }
        }
    }
}

private fun mitarbeiterMeinUrlaubTabletMonthCells(month: String): List<String> {
    val cells = mitarbeiterMonthCells(month)
    return cells + List((42 - cells.size).coerceAtLeast(0)) { "" }
}

@Composable
private fun MitarbeiterMeinUrlaubEventRow(label: String, value: String) {
    Div({ classes(MitarbeiterMeinUrlaubTabletStyles.requestEventRow) }) {
        Span({ classes(MitarbeiterMeinUrlaubTabletStyles.requestEventLabel) }) { Text(label) }
        Span({ classes(MitarbeiterMeinUrlaubTabletStyles.requestEventValue) }) { Text(value) }
    }
}

@Composable
private fun MitarbeiterUrlaubKennzahlen(allowance: org.example.project.screens_cus.Urlaubsanspruch?) {
    Div({ classes(MitarbeiterMeinUrlaubMobileStyles.metricsPanel) }) {
        Div({ classes(MitarbeiterMeinUrlaubMobileStyles.metricGroup) }) {
            Span({ classes(MitarbeiterMeinUrlaubMobileStyles.metricGroupTitle) }) { Text("Aktuelles Jahr") }
            Div({ classes(MitarbeiterMeinUrlaubMobileStyles.metricGrid) }) {
                MitarbeiterMeinUrlaubMetric("Anspruch", allowance?.gesamtanspruch?.let(::mitarbeiterUrlaubstageText) ?: "-")
                MitarbeiterMeinUrlaubMetric("Beantragt", allowance?.urlaubReserviert?.let(::mitarbeiterUrlaubstageText) ?: "-")
                MitarbeiterMeinUrlaubMetric("Genommen", allowance?.urlaubGenommen?.let(::mitarbeiterUrlaubstageText) ?: "-")
                MitarbeiterMeinUrlaubMetric("Verfügbar", allowance?.verfuegbar?.let(::mitarbeiterUrlaubstageText) ?: "-")
            }
        }
    }
}

@Composable
private fun MitarbeiterUrlaubAntragsbereiche(
    antraege: List<All>,
    selectedStatus: MitarbeiterUrlaubStatusGroup,
    userNamesById: Map<String, String>
) {
    val grouped = remember(antraege) {
        antraege.groupBy { mitarbeiterUrlaubStatusGroup(it) }
    }
    Div({ classes(MitarbeiterMeinUrlaubMobileStyles.listStack) }) {
        val visible = grouped[selectedStatus].orEmpty()
        Div({ classes(MitarbeiterMeinUrlaubMobileStyles.requestSection) }) {
            Div({ classes(MitarbeiterMeinUrlaubMobileStyles.requestSectionHeader) }) {
                Span { Text("${selectedStatus.label} ${visible.size}") }
            }
            if (visible.isEmpty()) {
                MitarbeiterMeinUrlaubHinweis("Keine Anträge in diesem Bereich.")
            } else {
                visible.forEach { antrag ->
                    MitarbeiterUrlaubAntragCard(antrag, userNamesById)
                }
            }
        }
    }
}

@Composable
private fun MitarbeiterUrlaubAntragCard(antrag: All, userNamesById: Map<String, String>) {
    val von = antrag.datumVon.ifBlank { antrag.datum }
    val bis = antrag.datumBis.ifBlank { von }
    val art = antrag.abwesenheitsart.ifBlank { antrag.bezeichnung.ifBlank { "Urlaub" } }
    val umfang = if (art.equals("Freizeitausgleich", ignoreCase = true)) {
        val stunden = when {
            antrag.zeitkontoStunden != 0.0 -> kotlin.math.abs(antrag.zeitkontoStunden)
            antrag.stundenDelta != 0.0 -> kotlin.math.abs(antrag.stundenDelta)
            else -> 0.0
        }
        if (stunden > 0.0) mitarbeiterMeinUrlaubStundenText(stunden) else "${mitarbeiterKalenderWerktage(von, bis).size} Arbeitstage"
    } else {
        "${mitarbeiterKalenderWerktage(von, bis).size} Arbeitstage"
    }
    Div({
        classes(MitarbeiterMeinUrlaubMobileStyles.listCard)
    }) {
        Span({ classes(MitarbeiterMeinUrlaubMobileStyles.cardTitle) }) {
            Text("$art von ${mitarbeiterMeinUrlaubFullDateLabel(von)} bis ${mitarbeiterMeinUrlaubFullDateLabel(bis)}")
        }
        Span({ classes(MitarbeiterMeinUrlaubMobileStyles.cardMeta) }) {
            Text(umfang)
        }
    }
}

private fun mitarbeiterMeinUrlaubFullDateLabel(iso: String): String {
    val parts = iso.split("-")
    if (parts.size != 3) return mitarbeiterKalenderDatumsLabel(iso)
    return "${parts[2]}.${parts[1]}.${parts[0]}"
}

private fun mitarbeiterMeinUrlaubStundenText(value: Double): String =
    if (value % 1.0 == 0.0) value.toInt().toString() else value.toString().replace(".", ",")

private fun mitarbeiterMeinUrlaubUserName(user: All): String =
    user.name.ifBlank {
        listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
    }.ifBlank { user.email.ifBlank { user.id } }


